import {
  Box,
  CircularProgress,
  Drawer,
  Typography,
  Divider,
  Button,
  Chip,
  IconButton,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import { useState } from "react";
import { Tooltip } from "@mui/material";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { useDispatch } from "react-redux";
import { usePermission } from "@/hooks/use-permissions";
import { CAPABILITIES } from "@/config/permissions";
import { useManualSettlement } from "@/hooks/use-manual-settlement";
import { getSettlementManualEligibility } from "@/utils/manual-settlement";
import { showToast } from "@/redux/actions/toast-actions";
import ManualSettlementDialog from "@/components/settlement/manual-settlement-dialog";
import { formatCurrency } from "@/utils/format-currency";
import { formatLabel } from "@/utils/format-label";
import { useSettlement } from "@/hooks/use-settlement";

const DrawerContent = styled(Box)(({ theme }) => ({
  width: "33vw",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  overflowY: "auto",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  letterSpacing: "0.05em",
  marginBottom: theme.spacing(1),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  paddingBlock: theme.spacing(1.2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-of-type": {
    borderBottom: "none",
  },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  flex: 1,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
  flex: 2,
  textAlign: "right",
  wordBreak: "break-word",
}));

export const StatusChip = styled(Chip)(({ status }) => ({
  borderRadius: "999px",
  fontWeight: 500,
  fontSize: "0.8rem",
  backgroundColor:
    status === "SETTLED"
      ? "#f0fdf4"
      : status === "PROCESSING"
        ? "#eff6ff"
        : status === "PENDING"
          ? "#fffbeb"
          : "#fef2f2",
  color:
    status === "SETTLED"
      ? "#15803d"
      : status === "PROCESSING"
        ? "#1e40af"
        : status === "PENDING"
          ? "#d97706"
          : "#dc2626",
}));

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    return format(new Date(dateStr), "MMM d, yyyy h:mm a");
  } catch {
    return dateStr;
  }
};

export function SettlementSheet({ settlement: rowSettlement, open, onClose }) {
  const { data: fetchedSettlement, isLoading } = useSettlement(
    open ? rowSettlement?.id : undefined,
  );
  const settlement = fetchedSettlement ?? rowSettlement;
  const dispatch = useDispatch();
  const canSettleManually = usePermission(CAPABILITIES.SETTLEMENT_MANUAL);
  const { eligible, reason, notice } = getSettlementManualEligibility(settlement);
  const settlementMutation = useManualSettlement();
  const [settleOpen, setSettleOpen] = useState(false);


  const handleSettle = (reference) => {
    settlementMutation.mutate(
      { paymentRefNo: reference },
      {
        onSuccess: (res) => {
          dispatch(showToast(res?.message ?? "Settlement processed", "success"));
          setSettleOpen(false);
        },
        onError: (err) =>
          dispatch(showToast(err?.message ?? "Settlement could not be processed", "error")),
      },
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          onClick: onClose,
        },
      }}
    >
      <DrawerContent>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              Settlement Details
              {isLoading && <CircularProgress size={14} thickness={5} />}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ref: {settlement?.settlementReferenceId ?? "N/A"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Actual Amount
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(settlement?.settlementGrossAmount)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fee: {formatCurrency(settlement?.totalFee)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Settled: {formatCurrency(settlement?.settlementNetAmount)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "flex-end",
            }}
          >
            <StatusChip
              label={formatLabel(settlement?.settlementStatus) ?? "N/A"}
              status={settlement?.settlementStatus}
              size="small"
            />
            <StatusChip
              label={`Payout: ${formatLabel(settlement?.payoutStatus) ?? "N/A"}`}
              status={settlement?.payoutStatus}
              size="small"
            />
          </Box>
        </Box>

        <SectionTitle>Merchant Information</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Merchant Name</InfoLabel>
            <InfoValue>{settlement?.merchantName ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Settlement Type</InfoLabel>
            <InfoValue>
              {formatLabel(settlement?.settlementType) ?? "N/A"}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Settlement Option</InfoLabel>
            <InfoValue>
              {formatLabel(settlement?.accountSettlementOption) ?? "N/A"}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Settlement Interval</InfoLabel>
            <InfoValue>
              {formatLabel(settlement?.settlementInterval) ?? "N/A"}
            </InfoValue>
          </InfoRow>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <SectionTitle>Settlement Account</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Account Name</InfoLabel>
            <InfoValue>
              {settlement?.settlementAccountName ?? "N/A"}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Account Number</InfoLabel>
            <InfoValue>
              {settlement?.settlementBeneficiaryAccount ?? "N/A"}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Beneficiary Account</InfoLabel>
            <InfoValue>
              {settlement?.settlementBeneficiaryAccount ?? "N/A"}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Bank Name</InfoLabel>
            <InfoValue>
              {settlement?.settlementAccount ?? "N/A"}
            </InfoValue>
          </InfoRow>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <SectionTitle>Settlement Timeline</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Date Created</InfoLabel>
            <InfoValue>{formatDate(settlement?.dateCreated)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Initiation Date</InfoLabel>
            <InfoValue>
              {formatDate(settlement?.settlementInitiationDate)}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Settlement Date</InfoLabel>
            <InfoValue>{formatDate(settlement?.dateSettled)}</InfoValue>
          </InfoRow>
        </Box>

        <Box sx={{ flex: 1 }} />

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ borderRadius: "999px", px: 3, minHeight: 42 }}
          >
            Close
          </Button>

          {canSettleManually && (
            <Tooltip title={eligible ? "" : (reason ?? "")} arrow placement="top">
              <span>
                <Button
                  variant="contained"
                  startIcon={<PaidOutlinedIcon />}
                  disabled={!eligible}
                  onClick={() => setSettleOpen(true)}
                  sx={{ borderRadius: "999px", px: 3, minHeight: 42 }}
                >
                  Settle Manually
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </DrawerContent>

      <ManualSettlementDialog
        open={settleOpen}
        onClose={() => setSettleOpen(false)}
        reference={settlement?.paymentRefNo}
        rows={[
          { label: "Settlement Ref", value: settlement?.settlementReferenceId },
          { label: "Payment Ref", value: settlement?.paymentRefNo },
          { label: "Merchant", value: settlement?.merchantName ?? settlement?.merchantId },
          { label: "Gross", value: formatCurrency(settlement?.settlementGrossAmount) },
          { label: "Fee", value: formatCurrency(settlement?.totalFee) },
          { label: "Net to settle", value: formatCurrency(settlement?.settlementNetAmount) },
        ]}
        notice={notice}
        mutation={settlementMutation}
        onConfirm={handleSettle}
      />
    </Drawer>
  );
}